import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  ExternalLink,
  ShieldCheck,
  Sparkles,
  Zap,
  RefreshCcw,
  HeadphonesIcon,
  Compass,
  Smartphone,
  Lock,
  UserCircle2,
  LogIn,
  UserPlus,
  Link2,
  LifeBuoy,
  BadgePercent,
  Newspaper,
  HelpCircle,
  CheckCircle2,
  Info,
  ChevronDown,
  MessageCircle,
  Send,
  Facebook,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  breadcrumb,
  faqSchema,
  organizationSchema,
  useSEO,
  websiteSchema,
} from "../lib/seo";
import { SITE, ASSETS, facebookLink, waLink, telegramLink } from "../lib/site";
import { trackCta, trackEvent } from "../lib/analytics";
import { FAQS } from "../data/faq";
import { getArticles } from "../data/articles";

const KEUNGGULAN = [
  { icon: Compass, title: "Akses Informasi yang Mudah", desc: "Susunan halaman ringkas memudahkan Anda menemukan informasi resmi tentang Sloto69 dengan cepat." },
  { icon: BookOpen, title: "Panduan Pengguna yang Jelas", desc: "Setiap panduan ditulis bertahap, sederhana, dan ramah untuk pengguna baru maupun lama." },
  { icon: Zap, title: "Tampilan Ringan dan Cepat", desc: "Website dioptimalkan agar terbuka cepat di berbagai perangkat dan kondisi jaringan." },
  { icon: RefreshCcw, title: "Update Link Alternatif", desc: "Halaman link alternatif menyajikan informasi terbaru sehingga akses tetap lancar." },
  { icon: HeadphonesIcon, title: "Dukungan Pengguna", desc: "Tim bantuan siap memberikan informasi resmi dan referensi yang Anda butuhkan." },
  { icon: Sparkles, title: "Sistem Navigasi Sederhana", desc: "Menu yang rapi membuat navigasi terasa intuitif tanpa elemen yang membingungkan." },
  { icon: Smartphone, title: "Kompatibel untuk Mobile", desc: "Tampilan responsif memastikan kenyamanan saat dibuka melalui smartphone atau tablet." },
  { icon: Lock, title: "Fokus pada Keamanan", desc: "Edukasi keamanan dan kenyamanan pengguna adalah inti komitmen kami." },
];

const FITUR = [
  { icon: UserCircle2, title: "Informasi Akun", desc: "Pelajari hal-hal penting seputar akun Anda dalam ekosistem Sloto69." },
  { icon: LogIn, title: "Panduan Login", desc: "Langkah login yang aman dan mudah dipahami." , to: "/panduan-login" },
  { icon: UserPlus, title: "Panduan Daftar", desc: "Proses pendaftaran lengkap dari awal hingga akhir." , to: "/panduan-daftar" },
  { icon: Link2, title: "Link Alternatif", desc: "Daftar URL cadangan saat link utama tidak dapat diakses." , to: "/link-alternatif" },
  { icon: LifeBuoy, title: "Pusat Bantuan", desc: "Akses kontak resmi tim bantuan kami melalui kanal yang tersedia." , to: "/kontak" },
  { icon: BadgePercent, title: "Informasi Promo", desc: "Kabar terkini seputar program promo dan informasi tambahan." },
  { icon: Newspaper, title: "Artikel Edukatif", desc: "Bacaan ringan untuk membantu pemahaman pengguna.", to: "/artikel" },
  { icon: HelpCircle, title: "FAQ Pengguna", desc: "Jawaban atas pertanyaan-pertanyaan yang sering diajukan." , to: "/faq" },
];

const PANDUAN_STEPS = [
  { num: "01", title: "Kunjungi halaman resmi Sloto69", desc: "Mulai dengan membuka halaman resmi Sloto69 melalui bookmark atau halaman link alternatif." },
  { num: "02", title: "Pilih menu login atau daftar", desc: "Tentukan tujuan Anda — login bagi pengguna lama atau daftar bagi pengguna baru." },
  { num: "03", title: "Ikuti instruksi yang tersedia", desc: "Lengkapi data sesuai instruksi yang ditampilkan di halaman dengan teliti." },
  { num: "04", title: "Simpan link resmi untuk akses berikutnya", desc: "Bookmark halaman resmi agar mudah diakses kembali kapan pun dibutuhkan." },
  { num: "05", title: "Hubungi bantuan jika mengalami kendala", desc: "Tim Sloto69 siap membantu melalui kanal kontak resmi yang tersedia." },
];

const KEAMANAN_TIPS = [
  "Pentingnya menggunakan link resmi.",
  "Hindari situs tiruan yang meniru tampilan resmi.",
  "Periksa alamat website sebelum login.",
  "Jangan membagikan data akun kepada pihak lain.",
  "Gunakan informasi dari halaman resmi.",
  "Aktifkan kebiasaan logout setelah selesai mengakses.",
];

export default function Home() {
  const articles = getArticles();

  useSEO({
    title: "Sloto69 | Company Profile dan Informasi Resmi",
    description:
      "Sloto69 adalah pusat informasi resmi berbahasa Indonesia dengan panduan login, daftar, link alternatif, keamanan, dan bantuan pengguna.",
    keywords: "sloto69, sloto69 login, sloto69 daftar, sloto69 link alternatif, sloto69 resmi, sloto69 indonesia, panduan sloto69, informasi sloto69, company profile sloto69",
    canonical: `${SITE.baseUrl}/`,
    image: `${SITE.baseUrl}${ASSETS.ogImage}`,
    jsonLd: [
      breadcrumb([
        { name: "Beranda", url: `${SITE.baseUrl}/` },
      ]),
      organizationSchema({
        site: SITE,
        logo: ASSETS.logo,
        sameAs: [facebookLink, telegramLink],
      }),
      websiteSchema({ site: SITE }),
      faqSchema(FAQS.map((f) => ({ q: f.q, a: f.a }))),
    ],
  });

  return (
    <>
      {/* HERO */}
      <section
        id="hero"
        data-testid="home-hero"
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 aurora-bg" />
        <div className="absolute inset-0 subtle-grid pointer-events-none" />
        <img
          src={ASSETS.hero}
          alt="Latar belakang abstrak premium Sloto69"
          loading="eager"
          fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/60 to-[#050505]" />

        <div className="shell relative pt-20 pb-24 sm:pt-28 sm:pb-32 lg:pt-32 lg:pb-40 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="gold-pill" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5" />
              Company Profile Resmi · Indonesia
            </span>
            <h1
              data-testid="hero-title"
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.05] tracking-tight"
            >
              Sloto69:{" "}
              <span className="gold-shimmer">Platform Digital Modern</span>{" "}
              dengan Informasi Lengkap, Akses Mudah, dan Pengalaman Pengguna yang Lebih Nyaman
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Temukan informasi lengkap seputar Sloto69, mulai dari panduan akun, akses login,
              fitur layanan, keamanan pengguna, hingga pembaruan link resmi dalam satu website
              company profile yang ringan dan mudah digunakan.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/tentang" data-testid="hero-cta-pelajari" className="btn-primary">
                Pelajari Sloto69 <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/panduan-login" data-testid="hero-cta-panduan" className="btn-secondary">
                <BookOpen className="w-4 h-4" /> Lihat Panduan
              </Link>
              <Link to="/link-alternatif" data-testid="hero-cta-link" className="btn-secondary">
                <ExternalLink className="w-4 h-4" /> Akses Link Resmi
              </Link>
            </div>

            <dl className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { k: "9+", v: "Halaman Informasi" },
                { k: "100%", v: "Bahasa Indonesia" },
                { k: "24/7", v: "Pusat Bantuan" },
              ].map((s) => (
                <div key={s.v} className="card-premium p-4">
                  <dt className="font-display text-2xl text-[#10B981]">{s.k}</dt>
                  <dd className="text-xs text-slate-400 mt-1">{s.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5 relative overflow-hidden rounded-3xl">
            <div className="absolute -inset-6 bg-[#10B981]/12 blur-3xl rounded-full pointer-events-none" />
            <div className="relative card-premium p-2 animate-float">
              <img
                src={ASSETS.dashboard}
                alt="Ilustrasi dashboard digital modern Sloto69"
                loading="eager"
                className="rounded-2xl w-full h-auto"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="card-premium p-4 flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                <span className="text-xs text-slate-300">Keamanan Digital</span>
              </div>
              <div className="card-premium p-4 flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-xs text-slate-300">Akses Cepat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TENTANG */}
      <section id="tentang" data-testid="home-about" className="relative section-pad">
        <div className="shell grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="gold-pill">Tentang Sloto69</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Pusat Informasi Resmi yang Dirancang untuk Pengguna Indonesia
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-300 leading-relaxed">
            <p>
              <strong className="text-white">Sloto69</strong> hadir sebagai brand digital
              berbahasa Indonesia yang berfokus pada kemudahan akses, kelengkapan informasi,
              kenyamanan pengguna, dan pembaruan informasi yang konsisten. Website ini berperan
              sebagai pusat informasi resmi yang menjelaskan bagaimana ekosistem layanan kami
              bekerja, dari panduan akun, akses login, fitur, hingga edukasi keamanan.
            </p>
            <p>
              Kami percaya bahwa pengalaman pengguna yang baik dimulai dari informasi yang
              jelas. Karena itu, setiap halaman pada website ini dirancang dengan pendekatan
              minimalis dan mobile-first, sehingga ringan diakses melalui berbagai perangkat
              tanpa mengurangi kelengkapan konten. Bahasa yang kami gunakan adalah bahasa
              Indonesia formal namun tetap ramah dibaca.
            </p>
            <p>
              Selain berfungsi sebagai company profile, halaman ini juga menjadi rujukan utama
              bagi pengguna yang ingin memahami praktik keamanan akun, mengenali ciri website
              resmi, dan memperoleh tautan alternatif saat kondisi jaringan kurang mendukung.
              Komitmen kami adalah menyediakan informasi yang transparan, jujur, dan tidak
              berlebihan.
            </p>
          </div>
        </div>
      </section>

      {/* KEUNGGULAN */}
      <section id="keunggulan" data-testid="home-benefits" className="relative section-pad bg-[#070710] border-y border-white/5">
        <div className="shell">
          <div className="max-w-2xl">
            <span className="gold-pill">Keunggulan</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Mengapa Sloto69 Berbeda dari Halaman Informasi Biasa
            </h2>
            <p className="mt-4 text-slate-300">
              Delapan keunggulan inti yang menjadikan Sloto69 sumber informasi terpercaya bagi
              pengguna Indonesia.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {KEUNGGULAN.map((k, i) => (
              <article
                key={k.title}
                data-testid={`benefit-card-${i}`}
                className="card-premium p-6"
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#10B981]/15 to-[#D4AF37]/10 border border-white/10">
                  <k.icon className="w-5 h-5 text-[#10B981]" aria-hidden />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{k.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{k.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FITUR UTAMA */}
      <section id="fitur" data-testid="home-features" className="relative section-pad">
        <div className="shell">
          <div className="max-w-2xl">
            <span className="gold-pill">Fitur Utama</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Semua yang Anda Butuhkan dalam Satu Website Sloto69
            </h2>
            <p className="mt-4 text-slate-300">
              Delapan kategori informasi yang dapat Anda jelajahi untuk memahami ekosistem
              Sloto69.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FITUR.map((f, i) => {
              const Inner = (
                <>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#0F172A] border border-[#D4AF37]/30">
                    <f.icon className="w-5 h-5 text-[#D4AF37]" aria-hidden />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                  {f.to && (
                    <span className="mt-4 inline-flex items-center gap-1 text-xs text-[#10B981]">
                      Pelajari <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  )}
                </>
              );
              return f.to ? (
                <Link
                  key={f.title}
                  to={f.to}
                  data-testid={`feature-card-${i}`}
                  className="card-premium p-6 block"
                >
                  {Inner}
                </Link>
              ) : (
                <article key={f.title} data-testid={`feature-card-${i}`} className="card-premium p-6">
                  {Inner}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PANDUAN PENGGUNA — TIMELINE */}
      <section id="panduan" data-testid="home-guide" className="relative section-pad bg-[#070710] border-y border-white/5">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="gold-pill">Panduan Pengguna</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Panduan Menggunakan Sloto69 dengan Mudah
            </h2>
            <p className="mt-4 text-slate-300">
              Lima langkah sederhana untuk memastikan Anda mendapatkan pengalaman yang nyaman
              saat mengakses informasi Sloto69.
            </p>
            <Link to="/panduan-login" data-testid="guide-cta" className="btn-secondary mt-6 inline-flex">
              Buka Panduan Lengkap <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <ol className="lg:col-span-8 relative space-y-4">
            <span className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-[#10B981]/40 via-white/10 to-transparent" aria-hidden />
            {PANDUAN_STEPS.map((s, i) => (
              <li key={s.num} data-testid={`guide-step-${i}`} className="relative flex gap-5 card-premium p-5 pl-7 ml-0">
                <div className="step-number w-12 h-12 rounded-xl flex items-center justify-center text-base flex-shrink-0">
                  {s.num}
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* KEAMANAN & KENYAMANAN */}
      <section id="keamanan" data-testid="home-security" className="relative section-pad">
        <div className="shell grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative overflow-hidden rounded-3xl">
            <div className="absolute -inset-6 bg-[#10B981]/12 blur-3xl rounded-full pointer-events-none" />
            <img
              src={ASSETS.security}
              alt="Ilustrasi shield keamanan digital Sloto69"
              loading="lazy"
              className="relative rounded-3xl w-full h-auto card-premium p-2"
            />
          </div>

          <div className="lg:col-span-7">
            <span className="gold-pill">Keamanan & Kenyamanan</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Edukasi Keamanan Digital adalah Komitmen Kami
            </h2>
            <p className="mt-4 text-slate-300">
              Dunia digital penuh dengan situs tiruan dan modus pengelabuan. Sloto69 percaya
              bahwa pengguna yang teredukasi akan lebih nyaman dan lebih aman dalam mengakses
              informasi resmi.
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {KEAMANAN_TIPS.map((tip, i) => (
                <li
                  key={tip}
                  data-testid={`security-tip-${i}`}
                  className="flex items-start gap-3 text-sm text-slate-300 card-premium p-4"
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10B981] flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>

            <Link to="/keamanan" data-testid="security-cta" className="btn-primary mt-8 inline-flex">
              Pusat Edukasi Keamanan <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ARTIKEL */}
      <section id="artikel" data-testid="home-articles" className="relative section-pad bg-[#070710] border-y border-white/5">
        <div className="shell">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="gold-pill">Informasi Terbaru</span>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                Artikel Edukatif Seputar Sloto69
              </h2>
              <p className="mt-4 text-slate-300">
                Bacaan ringan untuk membantu Anda memahami ekosistem layanan, panduan pengguna,
                hingga praktik keamanan akun yang sehat.
              </p>
            </div>
            <Link to="/artikel" data-testid="articles-view-all" className="btn-secondary">
              Lihat Semua Artikel <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((a, i) => (
              <article
                key={a.slug}
                data-testid={`article-card-${i}`}
                className="card-premium p-6 flex flex-col"
              >
                <span className="text-xs text-[#D4AF37] tracking-widest uppercase">
                  {a.category}
                </span>
                <h3 className="mt-3 font-display text-xl font-semibold leading-snug">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">
                  {a.excerpt}
                </p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-xs text-slate-500">{a.readTime}</span>
                  <Link
                    to={`/artikel/${a.slug}`}
                    data-testid={`article-read-${i}`}
                    onClick={() =>
                      trackEvent("read_article", {
                        event_category: "content",
                        event_label: a.title,
                        article_slug: a.slug,
                        location: "home_article_preview",
                      })
                    }
                    className="text-sm text-[#10B981] inline-flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    Baca Selengkapnya <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="home-faq" className="relative section-pad">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="gold-pill">Pertanyaan Umum</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              FAQ tentang Sloto69
            </h2>
            <p className="mt-4 text-slate-300">
              Jawaban singkat untuk pertanyaan yang paling sering diajukan oleh pengguna.
            </p>
          </div>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="grid gap-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="card-premium px-5 border-0"
                >
                  <AccordionTrigger className="text-left font-display text-base sm:text-lg hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* KONTAK */}
      <section id="kontak" data-testid="home-contact" className="relative section-pad bg-[#070710] border-t border-white/5">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="gold-pill">Kontak & Bantuan</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Tim Bantuan Siap Membantu Pengguna
            </h2>
            <p className="mt-4 text-slate-300">
              Tim bantuan siap membantu pengguna mendapatkan informasi yang dibutuhkan. Pilih
              kanal kontak yang paling nyaman bagi Anda.
            </p>
            <div className="mt-8 grid gap-3">
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" onClick={() => trackCta("click_whatsapp", "WhatsApp", "home_contact")} className="card-premium p-4 flex items-center gap-4 hover:border-[#10B981]/40">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="font-semibold">WhatsApp</p>
                  <p className="text-xs text-slate-400">Hubungi tim bantuan via WA</p>
                </div>
              </a>
              <a href={telegramLink} target="_blank" rel="noopener noreferrer" data-testid="contact-telegram" onClick={() => trackCta("click_telegram", "Telegram", "home_contact")} className="card-premium p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
                  <Send className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="font-semibold">Telegram</p>
                  <p className="text-xs text-slate-400">Akses kanal Telegram resmi</p>
                </div>
              </a>
              <a href={facebookLink} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" onClick={() => trackCta("click_facebook", "Facebook", "home_contact")} className="card-premium p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#1877F2]/15 flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-[#60A5FA]" />
                </div>
                <div>
                  <p className="font-semibold">Facebook</p>
                  <p className="text-xs text-slate-400">Gabung grup Facebook resmi</p>
                </div>
              </a>
              <Link to="/kontak" data-testid="contact-help" className="card-premium p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                  <LifeBuoy className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold">Halaman Bantuan</p>
                  <p className="text-xs text-slate-400">Buka halaman kontak lengkap</p>
                </div>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactForm() {
  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const nama = fd.get("nama") || "";
    const kontak = fd.get("kontak") || "";
    const pesan = fd.get("pesan") || "";
    const text = encodeURIComponent(
      `Halo Sloto69, saya ${nama} (${kontak}). ${pesan}`
    );
    trackEvent("submit_contact", {
      event_category: "form",
      event_label: "Home Contact Form",
      location: "home",
    });
    window.open(`https://api.whatsapp.com/send?phone=${SITE.whatsappNumber}&text=${text}`, "_blank", "noopener");
  };

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="card-premium p-6 lg:p-8 grid gap-4"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="grid gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-400">Nama</span>
          <input
            required
            type="text"
            name="nama"
            data-testid="contact-input-nama"
            placeholder="Nama lengkap Anda"
            className="bg-[#0F172A]/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
          />
        </label>
        <label className="grid gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-400">Kontak</span>
          <input
            required
            type="text"
            name="kontak"
            data-testid="contact-input-kontak"
            placeholder="Nomor WhatsApp Anda"
            className="bg-[#0F172A]/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
          />
        </label>
      </div>
      <label className="grid gap-2">
        <span className="text-xs uppercase tracking-wider text-slate-400">Pesan</span>
        <textarea
          required
          name="pesan"
          data-testid="contact-input-pesan"
          rows={5}
          placeholder="Tulis pesan Anda di sini..."
          className="bg-[#0F172A]/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
        />
      </label>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-slate-400 inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" /> Pesan akan diteruskan ke WhatsApp resmi.
        </p>
        <button
          type="submit"
          data-testid="contact-submit"
          className="btn-primary"
        >
          Kirim Pesan <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
